import React from 'react';
import styled from 'styled-components';
import { Badge } from 'reactstrap';
import { CampaignReduced, CampaignStatus } from '../../models';
import StyledTable from '../../components/StyledTable';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { campaignChannelLabel, formatCampaignDateTime, getCampaignExecutionSummary } from './campaignsUtils';
import { CampaignActionsMenu } from './CampaignActionsMenu';
import { CampaignStatusBadge } from './CampaignStatusBadge';


const EmptyState = styled.div`
    padding: var(--spacer-L);
    text-align: center;
    color: var(--text-muted);
    border: 1px dashed var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    background: white;
`;

const CampaignName = styled.div`
    font-weight: 800;
    color: var(--text-body);
`;

const CampaignMeta = styled.div`
    color: var(--text-muted);
    font-size: 0.82rem;
    margin-top: var(--spacer-XXS);
`;

const ChannelBadge = styled(Badge)`
    background: rgba(42, 183, 175, 0.12);
    color: var(--secondary-color, #009385);
    border: 1px solid rgba(42, 183, 175, 0.35);
    font-size: 0.72rem;
`;

const RecipientsBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: rgba(0, 147, 133, 0.12);
    color: var(--secondary-color, #009385);
    font-weight: 800;
    font-size: 0.8rem;
`;

export interface CampaignsTableProps {
    campaigns: CampaignReduced[];
    loading: boolean;
    hasFilters: boolean;
    onEdit: (campaign: CampaignReduced) => void;
    onAnalyze: (campaign: CampaignReduced) => void;
    onDuplicate: (campaign: CampaignReduced) => void;
    onArchiveOrDelete: (campaign: CampaignReduced) => void;
}

export const CampaignsTable: React.FC<CampaignsTableProps> = ({
    campaigns,
    loading,
    hasFilters,
    onEdit,
    onAnalyze,
    onDuplicate,
    onArchiveOrDelete,
}) => {
    const t = useTranslations();

    if (!loading && !campaigns.length && !hasFilters) {
        return <EmptyState>{t(TK.campaignsEmptyState)}</EmptyState>;
    }

    return (
        <StyledTable scrollable hover>
                <thead>
                    <tr>
                        <th>{t(TK.name)}</th>
                        <th>{t(TK.status)}</th>
                        <th>{t(TK.channel)}</th>
                        <th>{t(TK.changed)}</th>
                        <th>{t(TK.campaignLastTriggered)}</th>
                        <th>{t(TK.campaignEstimatedRecipients)}</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {campaigns.map(campaign => {
                        const executionSummary = getCampaignExecutionSummary(campaign);
                        const canDelete = campaign.status === CampaignStatus.Draft || campaign.status === CampaignStatus.Ready;
                        const archiveOrDeleteLabel = canDelete ? TK.delete : TK.archive;

                        return (
                            <tr key={campaign.id} style={{ cursor: 'pointer' }} onClick={() => onEdit(campaign)}>
                                <td>
                                    <CampaignName>{campaign.name}</CampaignName>
                                    <CampaignMeta>
                                        {(campaign.createdByName || campaign.createdBy) ? `${t(TK.createdBy)}: ${campaign.createdByName ?? campaign.createdBy}` : t(TK.notAvailable)}
                                    </CampaignMeta>
                                </td>
                                <td><CampaignStatusBadge status={campaign.status} /></td>
                                <td><ChannelBadge>{t(campaignChannelLabel(campaign.channel))}</ChannelBadge></td>
                                <td>{formatCampaignDateTime(campaign.lastUpdateDt)}</td>
                                <td>
                                    {campaign.lastTriggeredAt
                                        ? (
                                            <div>
                                                <div>{formatCampaignDateTime(campaign.lastTriggeredAt)}</div>
                                                <CampaignMeta>{t(executionSummary.labelKey)}</CampaignMeta>
                                            </div>
                                        )
                                        : t(TK.campaignNeverSent)}
                                </td>
                                <td>
                                    <RecipientsBadge>{campaign.lastRecipientEstimate ?? 0}</RecipientsBadge>
                                </td>
                                <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                    <CampaignActionsMenu
                                        campaign={campaign}
                                        onEdit={() => onEdit(campaign)}
                                        onAnalyze={() => onAnalyze(campaign)}
                                        onDuplicate={() => onDuplicate(campaign)}
                                        onArchiveOrDelete={() => onArchiveOrDelete(campaign)}
                                        onArchiveOrDeleteLabel={archiveOrDeleteLabel}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
        </StyledTable>
    );
}
